import React, { useState } from "react";
import PropTypes from "prop-types";
import { SKILL_LEVELS } from "../constants";
import AvatarCreatorModal from "./AvatarCreatorModal";

export default function ProfileSettingsModal({ 
  show, 
  onClose, 
  onSubmit, 
  profileData, 
  onChange,
  isOnboarding = false 
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showAvatarCreator, setShowAvatarCreator] = useState(false);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await onSubmit(e);
      setShowAvatarCreator(false);
      if (!isOnboarding) {
        onClose();
      }
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarSave = (avatarData) => {
    if (avatarData && avatarData.url) {
      onChange("avatar_url", avatarData.url);
    }
    setShowAvatarCreator(false);
  };

  // Create a default avatar URL
  const defaultAvatarUrl = "https://avataaars.io/?topType=ShortHairShortFlat&accessoriesType=Blank&hairColor=Brown&facialHairType=Blank&clotheType=BlazerSweater&eyeType=Default&eyebrowType=Default&mouthType=Smile&skinColor=Light";

  // Use avatar_url with fallback to default
  const displayAvatarUrl = profileData.avatar_url || defaultAvatarUrl;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg space-y-4 overflow-y-auto max-h-[90vh]"
        >
          <h3 className="text-xl font-bold text-center mb-4 text-textDark">
            {isOnboarding ? 'Complete Your Profile' : 'Profile Settings'}
          </h3>

          {isOnboarding && (
            <p className="text-center text-gray-600 mb-6">
              Welcome to TennisNow! Let's set up your profile so other players can get to know you.
            </p>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Avatar Section */}
          <div className="flex flex-col items-center space-y-4 mb-6">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
              <img
                src={displayAvatarUrl}
                alt="Profile avatar"
                className="w-full h-full"
                key={displayAvatarUrl}
              />
            </div>
            {!isOnboarding && (
              <button
                type="button"
                onClick={() => setShowAvatarCreator(true)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span>Create Avatar</span>
              </button>
            )}
          </div>

          {/* Form fields */}
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <label className="text-sm font-semibold text-textDark">
                  First Name <span className="text-red-600">*</span>
                </label>
                <label className="text-sm font-semibold text-textDark">
                  Last Initial <span className="text-red-600">*</span>
                </label>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <input
                  type="text"
                  value={profileData.first_name || ''}
                  onChange={(e) => onChange("first_name", e.target.value)}
                  className="col-span-3 border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="First Name"
                  required
                />
                <input
                  type="text"
                  value={profileData.last_name || ''}
                  onChange={(e) => onChange("last_name", e.target.value.charAt(0).toUpperCase())}
                  className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-center"
                  placeholder="Initial"
                  maxLength="1"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1 text-textDark">
                Email <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                value={profileData.email || ''}
                onChange={(e) => onChange("email", e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1 text-textDark">
                Phone <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                value={profileData.phone_number || ''}
                onChange={(e) => onChange("phone_number", e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1 text-textDark">
                Gender <span className="text-red-600">*</span>
              </label>
              <select
                value={profileData.gender || 'male'}
                onChange={(e) => onChange("gender", e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                required
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1 text-textDark">
                Skill Level <span className="text-red-600">*</span>
              </label>
              <select
                value={profileData.skill_level || '2.5'}
                onChange={(e) => onChange("skill_level", e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                required
              >
                {Object.entries(SKILL_LEVELS).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            {!isOnboarding && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primaryDark disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isOnboarding ? 'Next' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      <AvatarCreatorModal
        show={showAvatarCreator}
        onClose={() => setShowAvatarCreator(false)}
        onSave={handleAvatarSave}
      />
    </>
  );
}

ProfileSettingsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  profileData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  isOnboarding: PropTypes.bool
}; 